import { format, formatDistance } from 'date-fns'

import { Market } from '../../../../features/markets/types/Market'
import { FeatureImportFormValues, ImportStatus, WeightImportFormValues } from '../types/ImportTool'

export const importStatusPollingInterval = 15000

export const toImportStatus = (data?: Partial<ImportStatus> | null): ImportStatus => ({
  updating: data?.updating ?? false,
  previousSucceeded: data?.previousSucceeded ?? true,
  started: data?.started ?? 0,
  ended: data?.ended ?? 0,
})

export type ImportStatusSummary =
  | { state: 'never' }
  | { state: 'updating'; started: string; elapsed: string }
  | { state: 'succeeded' | 'failed'; started: string; duration: string }

const formatStarted = (timestamp: number) => format(timestamp, 'MMMM d, yyyy h:mm a')

export const describeImportStatus = (status: ImportStatus, now: number): ImportStatusSummary => {
  if (status.updating) return { state: 'updating', started: formatStarted(status.started), elapsed: formatDistance(status.started, now) }
  if (status.started <= 0) return { state: 'never' }
  return {
    state: status.previousSucceeded ? 'succeeded' : 'failed',
    started: formatStarted(status.started),
    duration: formatDistance(status.started, Math.max(status.ended, status.started)),
  }
}

export const sortMarketsByName = (markets: Market[]) => [...markets].sort((a, b) => a.name.localeCompare(b.name))

export const toWeightImportName = (date: Date, storeName: string, marketIso: string) =>
  `${format(date, 'dd_MM_yyyy')}_${storeName.replace(/ /g, '_')}_${marketIso.toUpperCase()}`

export const toWeightImportDate = (date: Date) => format(date, 'yyyyMMdd')

// The fields and their order follow the legacy multipart forms.
export const toWeightImportBody = (values: WeightImportFormValues): FormData => {
  const date = values.date ?? new Date()
  const body = new FormData()
  body.append('base', values.base.trim())
  body.append('store', values.store)
  body.append('test', values.test)
  body.append('updategps', values.updateGps)
  if (values.file) body.append('file', values.file)
  body.append('name', toWeightImportName(date, values.store, values.market))
  body.append('date', toWeightImportDate(date))
  body.append('market', values.market)
  return body
}

export const toFeatureImportBody = (values: FeatureImportFormValues): FormData => {
  const body = new FormData()
  body.append('store', values.store)
  body.append('test', values.test)
  if (values.file) body.append('file', values.file)
  body.append('market', values.market)
  return body
}

export const emptyWeightImportValues = (): WeightImportFormValues => ({
  base: '',
  store: '',
  market: '',
  date: new Date(),
  test: '',
  updateGps: '',
  file: null,
})

export const emptyFeatureImportValues = (): FeatureImportFormValues => ({ market: '', store: '', test: '', file: null })
